/**
 * @flow
 */

import gql from 'graphql-tag';
import { enableExperimentalFragmentVariables } from 'graphql-tag';
import { injectIntl } from 'react-intl';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withProps } from 'recompose';
import moment from 'moment';
import {
    get,
    find,
    some,
    cloneDeep,
    remove,
    findIndex,
    map,
    each,
    filter,
    isEmpty,
} from 'lodash';

import apollo from '../../../../apollo-client';
import CabinetTable from '../../components/timetable/CabinetTable';
import { setSettings } from '../../../../actions/salon';

import {
    APPOINTMENT_FRAGMENT,
    APPOINTMENT_ADD_SUBSCRIPTION,
    APPOINTMENT_DELETE_SUBSCRIPTION,
} from '../queries';

import { MASTER_CABINET_TABLE_FRAGMENT } from '../masterAccessQueries';

// The query to get current salon object
export const GET_SALON_QUERY = gql`
    query getSalon(
        $date: String!
        $salon: ID!
        $showArchive: Boolean
        $lang: String!
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    name
                    administrator {
                        id
                    }
                    settings {
                        schedule {
                            startAt
                            endAt
                            step
                            format
                        }
                        enableArchive
                    }
                    cabinets(date: $date) {
                        id
                        name(lang: $lang)
                        schedule {
                            startAt
                            endAt
                        }
                        appointments(date: $date, showArchive: $showArchive) {
                            id
                            ...appointment
                        }
                    }
                }
            }
            ...onMasterCabinetTable
        }
    }

    ${APPOINTMENT_FRAGMENT}
    ${MASTER_CABINET_TABLE_FRAGMENT}
`;

/**
 * Handle new appointment subscription and update local cache with new appointment
 */
const subscribeToAddedAppointment = subscribeToMore => params => {
    return subscribeToMore({
        document: APPOINTMENT_ADD_SUBSCRIPTION,
        variables: {
            salon: params.salon,
            date: params.date,
            lang: params.currentLanguage,
        },
        // Update local cache with a new appointment
        updateQuery: (prev, { subscriptionData }) => {
            if (!subscriptionData) {
                return prev;
            }

            const next = cloneDeep(prev);

            const appointment = subscriptionData.data.appointmentAdded;

            if (
                (params.showArchive || !appointment.archived) &&
                appointment.cabinet &&
                some(next.viewer.salon.cabinets, {
                    id: appointment.cabinet.id,
                })
            ) {
                each(next.viewer.salon.cabinets, (cabinet, key) => {
                    const index = findIndex(cabinet.appointments, [
                        'id',
                        appointment.id,
                    ]);
                    if (index > -1) {
                        if (appointment.cabinet.id == cabinet.id) {
                            next.viewer.salon.cabinets[key].appointments[
                                index
                            ] = appointment;
                        } else {
                            remove(
                                next.viewer.salon.cabinets[key].appointments,
                                ['id', appointment.id],
                            );
                        }
                    } else {
                        if (appointment.cabinet.id == cabinet.id) {
                            next.viewer.salon.cabinets[key].appointments.push(
                                appointment,
                            );
                        }
                    }
                });
            }

            return next;
        },
    });
};

/**
 * Handle deleted appointments subscription and remove data from local cache
 */
const subscribeToDeletedAppointment = subscribeToMore => params => {
    return subscribeToMore({
        document: APPOINTMENT_DELETE_SUBSCRIPTION,
        variables: {
            salon: params.salon,
            date: params.date,
            lang: params.currentLanguage,
        },
        updateQuery: (prev, { subscriptionData }) => {
            if (!subscriptionData) {
                return prev;
            }

            const next = cloneDeep(prev);

            const appointment = subscriptionData.data.appointmentDeleted;

            let cabinet = null;
            // check if appointment has cabinet (related to #312)
            if (appointment.cabinet) {
                cabinet = find(next.viewer.salon.cabinets, {
                    id: appointment.cabinet.id,
                });
            }

            if (cabinet) {
                remove(cabinet.appointments, o => o.id === appointment.id);
            }

            return next;
        },
    });
};

const withData = graphql(GET_SALON_QUERY, {
    options: ownProps => ({
        variables: {
            date: moment(ownProps.date).format('YYYY-MM-DD'),
            salon: ownProps.salonId,
            showArchive: ownProps.showArchive,
            lang: ownProps.currentLanguage,
        },
        fetchPolicy: 'cache-and-network',
    }),
    props: ({ ownProps, data: { loading, viewer, subscribeToMore } }) => {
        const salon = get(viewer, 'salon');
        const cabinets = filter(
            get(salon, 'cabinets'),
            cabinet => !!cabinet.schedule.length,
        );

        // Set settings of the other salon in SysAdmin mode
        if (
            !loading &&
            viewer &&
            ownProps.isSysadmin &&
            !isEmpty(salon) &&
            salon.administrator.id !== ownProps.user
        ) {
            const salonSettings = Object.assign(
                { enableArchive: get(salon, 'settings.enableArchive') },
                get(salon, 'settings.schedule'),
            );
            ownProps.setSettings(salonSettings);
        }

        return {
            loading,
            salon,
            cabinets,

            // Subscribe on appointments updates
            subscribeToAddedAppointment: subscribeToAddedAppointment(
                subscribeToMore,
            ),
            subscribeToDeletedAppointment: subscribeToDeletedAppointment(
                subscribeToMore,
            ),
        };
    },
});

const mapStateToProps = ({ appointments, user, salon, intl }) => ({
    user: user.get('id'),
    date: appointments.get('date'),
    salonId: user.get('salon'),
    showArchive: salon.get('showArchive'),
    currentLanguage: intl.get('locale') || intl.get('defaultLanguage'),
    allowToAddAppointments:
        user.get('isOwner') ||
        moment().isSameOrBefore(appointments.get('date'), 'day'),
    isSysadmin: user.get('isSysadmin'),
});

const mapDipatchToProps = {
    setSettings,
};

/**
 * Make subscriptions to appointment related events when the component has mounted
 */
const withLifecycle = function() {
    // store unsubscribe handlers in a closure
    let unsubscribeToAddedAppointment,
        unsubscribeToDeletedAppointment,
        unsubscribeToMigratedMaster;
    return lifecycle({
        componentWillMount: function() {
            const {
                subscribeToAddedAppointment,
                subscribeToDeletedAppointment,
                subscribeToMigratedMaster,
                salonId,
                date,
                showArchive,
                currentLanguage,
            } = this.props;

            // save appointment add unsubscribe handler to use later
            unsubscribeToAddedAppointment = subscribeToAddedAppointment({
                salon: salonId,
                date,
                showArchive,
                currentLanguage,
            });
            // save appointment delete unsubscribe handler to use later
            unsubscribeToDeletedAppointment = subscribeToDeletedAppointment({
                salon: salonId,
                date,
                currentLanguage,
            });
        },
        componentDidUpdate: function(prevProps) {
            const {
                subscribeToAddedAppointment,
                subscribeToDeletedAppointment,
                subscribeToMigratedMaster,
                salonId,
                date,
                showArchive,
                currentLanguage,
            } = this.props;
            // resubscribe if salon or date has changed
            if (
                !moment(date).isSame(prevProps.date, 'day') ||
                salonId !== prevProps.salonId
            ) {
                // unsubscribe if handler is available
                if (unsubscribeToAddedAppointment) {
                    unsubscribeToAddedAppointment();
                }
                // subscribe and save unsubscribe handler
                unsubscribeToAddedAppointment = subscribeToAddedAppointment({
                    salon: salonId,
                    date,
                    showArchive,
                    currentLanguage,
                });
                // unsubscribe if handler is available
                if (unsubscribeToDeletedAppointment) {
                    unsubscribeToDeletedAppointment();
                }
                // subscribe and save unsubscribe handler
                unsubscribeToDeletedAppointment = subscribeToDeletedAppointment(
                    {
                        salon: salonId,
                        date,
                        currentLanguage,
                    },
                );
            }
        },
    });
};

const handlers = withHandlers({
    updateAppointmentClient: ({
        salonId,
        date,
        showArchive,
        currentLanguage,
    }) => client => {
        const variables = {
            date: moment(date).format('YYYY-MM-DD'),
            salon: salonId,
            showArchive,
            lang: currentLanguage,
        };
        const data = apollo.cache.readQuery({
            query: GET_SALON_QUERY,
            variables,
        });
        data.viewer.salon.cabinets.forEach(cabinet => {
            cabinet.appointments.forEach(appointment => {
                if (appointment.client.id === client.id) {
                    appointment.notes = client.notes;
                }
            });
        });
        apollo.cache.writeQuery({
            query: GET_SALON_QUERY,
            variables,
            data,
        });
    },
    createAppointments: ({
        salonId,
        date,
        showArchive,
        currentLanguage,
    }) => appointments => {
        const data = apollo.cache.readQuery({
            query: GET_SALON_QUERY,
            variables: {
                date: moment(date).format('YYYY-MM-DD'),
                salon: salonId,
                showArchive,
                lang: currentLanguage,
            },
        });

        each(appointments, appointment => {
            const cabinet = find(
                data.viewer.salon.cabinets,
                i => i.id === appointment.cabinet.id,
            );

            if (cabinet) {
                cabinet.appointments.push({
                    ...appointment,
                    invoice: null,
                });
            }
        });

        apollo.cache.writeQuery({
            query: GET_SALON_QUERY,
            variables: {
                date: moment(date).format('YYYY-MM-DD'),
                salon: salonId,
                showArchive,
                lang: currentLanguage,
            },
            data,
        });
    },
    archiveAppointment: ({
        salonId,
        showArchive,
        currentLanguage,
        date,
    }) => appointment => {
        const variables = {
            salon: salonId,
            showArchive,
            date: moment(date).format('YYYY-MM-DD'),
            lang: currentLanguage,
        };
        const data = apollo.cache.readQuery({
            query: GET_SALON_QUERY,
            variables,
        });
        const cabinet = find(
            data.viewer.salon.cabinets,
            i => i.id === appointment.cabinet.id,
        );
        remove(cabinet.appointments, item => item.id === appointment.id);
        apollo.cache.writeQuery({
            query: GET_SALON_QUERY,
            variables,
            data,
        });
    },
});

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDipatchToProps,
    ),
    withData,
    withLifecycle(),
    handlers,
)(CabinetTable);
